import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { Editor } from "@tinymce/tinymce-react";
import { FolderOpen, Plus, Trash2 } from "lucide-react";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";
import MediaPickerModal from "./MediaPickerModal";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  pairs: [
    { url: "", alt_fa: "", alt_en: "", label_fa: "", label_en: "" },
    { url: "", alt_fa: "", alt_en: "", label_fa: "", label_en: "" },
  ],
  shufflePairs: true,
  layout: "left-right",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";

  const body = item.body || {};
  const rawPairs = Array.isArray(body.pairs) ? body.pairs : [];

  const pairs =
    rawPairs.length > 0
      ? rawPairs.map((p) => ({
          url: p?.image?.url || "",
          alt_fa: p?.image?.alt?.fa || "",
          alt_en: p?.image?.alt?.en || "",
          label_fa: p?.label?.fa || "",
          label_en: p?.label?.en || "",
        }))
      : makeEmptyForm().pairs;

  const shufflePairs = !!body.shuffle;
  const layout = body.layout || "left-right";
  const points = item?.points ?? 1;
  const status = item?.status || "published";
  const tags = Array.isArray(item?.tags) ? item.tags.join(",") : "";
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    pairs,
    shufflePairs,
    layout,
    points,
    status,
    tags,
    explanation_fa,
    explanation_en,
  };
};

export default function ImageMatchingModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm);
  const [fileModalOpen, setFileModalOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) setForm(itemToForm(item));
    else setForm(makeEmptyForm());
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const addPair = () =>
    setForm((f) => ({
      ...f,
      pairs: [
        ...f.pairs,
        { url: "", alt_fa: "", alt_en: "", label_fa: "", label_en: "" },
      ],
    }));

  const removePair = (idx) =>
    setForm((f) => {
      if (f.pairs.length <= 2) return f;
      const nextPairs = f.pairs.filter((_, i) => i !== idx);
      return { ...f, pairs: nextPairs };
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("image_matching.toasts.stem_required"));
      return;
    }
    const validPairs = form.pairs.filter(
      (p) => p.url.trim() && (p.label_fa.trim() || p.label_en.trim()),
    );
    if (validPairs.length < 2) {
      toast.error(t("image_matching.toasts.need_two_pairs"));
      return;
    }

    const payload = {
      kind: "image_matching",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: { fa: form.stem_fa || undefined, en: form.stem_en || undefined },
      body: {
        pairs: validPairs.map((p) => ({
          image: {
            url: p.url,
            alt: { fa: p.alt_fa || undefined, en: p.alt_en || undefined },
          },
          label: { fa: p.label_fa || undefined, en: p.label_en || undefined },
        })),
        shuffle: !!form.shufflePairs,
        layout: form.layout,
      },
      points: Number(form.points) || 1,
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t(
              "image_matching.toasts.updated",
              "سؤال Image Matching با موفقیت ویرایش شد",
            )
          : t(
              "image_matching.toasts.created",
              "سؤال Image Matching با موفقیت ساخته شد",
            ),
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <>
      <MediaPickerModal
        isOpen={fileModalOpen}
        onClose={() => setFileModalOpen(false)}
        token={token}
      />
      <form onSubmit={submit} className="space-y-4">
        {/* Stem */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div dir={isRtl ? "rtl" : "ltr"}>
            <label className="block text-sm font-medium mb-1">
              {t("image_matching.stem_fa")}
            </label> 
            <CustomEditor
              value={form.stem_fa}
              onEditorChange={(content) =>
                handleEditorChange("stem_fa", content)
              }
              directionality={isRtl ? "rtl" : "ltr"}
              placeholder={t("image_matching.stem_fa_ph")}
            />
          </div>
          <div dir="ltr">
            <label className="block text-sm font-medium mb-1">
              {t("image_matching.stem_en")}
            </label>
            <CustomEditor
              value={form.stem_en}
              onEditorChange={(content) =>
                handleEditorChange("stem_en", content)
              }
              placeholder={t("image_matching.stem_en_ph")}
            />
          </div>
        </div>

        {/* Layout Selector */}
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("image_matching.layout")}
          </label>
          <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
            {["left-right", "right-left", "top-bottom", "bottom-top"].map(
              (layout) => (
                <div key={layout} className="flex items-center">
                  <input
                    type="radio"
                    id={`layout-${layout}`}
                    name="layout"
                    value={layout}
                    checked={form.layout === layout}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, layout: e.target.value }))
                    }
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                  />
                  <label
                    htmlFor={`layout-${layout}`}
                    className="ml-2 text-sm text-gray-700"
                  >
                    {t(`image_matching.layout_${layout}`)}
                  </label>
                </div>
              ),
            )}
          </div>
        </div>

        {/* Pairs */}
        <div>
          <button
            type="button"
            onClick={() => setFileModalOpen(true)}
            className="px-3 py-2 rounded-lg border bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2 whitespace-nowrap"
            title={t("media_picker.title", "Browse")}
          >
            <FolderOpen className="w-4 h-4" />
            {t("media_picker.title", "Browse")}
          </button>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium">
              {t("image_matching.pairs")}
            </label>
            <button
              type="button"
              className="text-blue-600 text-sm"
              onClick={addPair}
            >
              <Plus size={16} className="inline-block mr-1" />
              {t("image_matching.add_pair")}
            </button>
          </div>
          <div className="space-y-2">
            {form.pairs.map((p, idx) => (
              <div key={idx} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold">
                    {t("image_matching.pair_n", { n: idx + 1 })}
                  </span>
                  {form.pairs.length > 2 && (
                    <button
                      type="button"
                      className="p-1 rounded-full text-red-600 hover:bg-red-100"
                      onClick={() => removePair(idx)}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input
                    className="border rounded-lg px-3 py-2"
                    placeholder={t("image_matching.url_ph")}
                    value={p.url}
                    onChange={(e) => {
                      const pairs = [...form.pairs];
                      pairs[idx].url = e.target.value;
                      setForm((f) => ({ ...f, pairs }));
                    }}
                  />
                  <input
                    className="border rounded-lg px-3 py-2"
                    placeholder={t("image_matching.alt_fa_ph")}
                    value={p.alt_fa}
                    onChange={(e) => {
                      const pairs = [...form.pairs];
                      pairs[idx].alt_fa = e.target.value;
                      setForm((f) => ({ ...f, pairs }));
                    }}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input
                    className="border rounded-lg px-3 py-2"
                    placeholder={t("image_matching.alt_en_ph")}
                    value={p.alt_en}
                    onChange={(e) => {
                      const pairs = [...form.pairs];
                      pairs[idx].alt_en = e.target.value;
                      setForm((f) => ({ ...f, pairs }));
                    }}
                  />
                  <input
                    className="border rounded-lg px-3 py-2"
                    placeholder={t("image_matching.label_fa_ph")}
                    value={p.label_fa}
                    onChange={(e) => {
                      const pairs = [...form.pairs];
                      pairs[idx].label_fa = e.target.value;
                      setForm((f) => ({ ...f, pairs }));
                    }}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input
                    className="border rounded-lg px-3 py-2"
                    placeholder={t("image_matching.label_en_ph")}
                    value={p.label_en}
                    onChange={(e) => {
                      const pairs = [...form.pairs];
                      pairs[idx].label_en = e.target.value;
                      setForm((f) => ({ ...f, pairs }));
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-1">
            <label className="block text-sm font-medium mb-1">
              {t("image_matching.shuffle")}
            </label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={String(form.shufflePairs)}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  shufflePairs: e.target.value === "true",
                }))
              }
            >
              <option value="true">{t("image_matching.shuffle_yes")}</option>
              <option value="false">{t("image_matching.shuffle_no")}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("image_matching.points")}
            </label>
            <input
              type="number"
              min={0}
              className="w-full border rounded-lg px-3 py-2"
              value={form.points}
              onChange={(e) =>
                setForm((f) => ({ ...f, points: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("image_matching.status")}
            </label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value }))
              }
            >
              <option value="published">{t("common.published")}</option>
              <option value="draft">{t("common.draft")}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("image_matching.tags")}
            </label>
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder={t("common.tags_csv")}
              value={form.tags}
              onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
            />
          </div>
        </div>

        <ExplanationField
          valueFa={form.explanation_fa}
          valueEn={form.explanation_en}
          onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
          onChangeEn={(v) => handleEditorChange("explanation_en", v)}
          isRtl={isRtl}
        />

        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg"
        >
          {isEdit ? t("common.update") : t("common.create")}
        </button>
      </form>
    </>
  );
}
